<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="scrollElement" :class="containerStyles">
    <div
      class="mb-8 grid-cols-12 col-start-2 col-span-12 flex justify-between items-end md:mt-36"
    >
      <header2 v-if="title" :text="title" :is-umbraco="isUmbraco" :theme="themeType" />
      <link-button
        v-if="ctaLink"
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        :theme="themeType"
        type="secondary"
        classes="md:inline-block"
      />
    </div>
    <div v-if="projectCards.length > 1" class="hidden col-start-2 col-span-12 md:flex">
      <control-button
        @click="prev"
        pos="left"
        classes="mr-3"
        :theme-type="themeType"
        :disabled="currentIndex === 0"
      />
      <control-button
        @click="next"
        pos="right"
        :theme-type="themeType"
        :disabled="currentIndex === projectCards.length - 1"
      />
    </div>
    <div
      class="mb-8 col-start-1 col-span-full flex justify-between items-end md:mt-[60px]"
    >
      <carousel ref="carouselRef" v-bind="settings" :breakpoints="breakpoints">
        <slide v-for="(project, index) in projectCards" :key="project.title">
          <a class="carousel__item group overflow-hidden w-full" :href="project.url">
            <image-component
              isLink
              :image="project.image"
              :img-alt="project.imgAlt"
              classes="w-full h-full object-cover aspect-[2/1]"
            />
            <div class="flex text-left gap-6 mt-8">
              <paragraph
                v-if="project.tag"
                :text="project.tag"
                :is-umbraco="isUmbraco"
                :theme="themeType"
                classes="font-bold"
              />
            </div>
            <header4
              :text="project.title"
              :is-umbraco="isUmbraco"
              :theme="themeType"
              classes="mt-2 text-left w-full"
            />
          </a>
        </slide>
      </carousel>
    </div>
    <div class="md:hidden col-start-2 col-span-12 flex justify-center">
      <link-button
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        :theme="themeType"
        :is-umbraco="isUmbraco"
        type="secondary"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide } from "vue3-carousel";
import {
  computed,
  toRefs,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  onBeforeMount,
  watch,
} from "vue";
import { changeTheme, getThemeType } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import ImageComponent from "@atoms/Image/Img.vue";
import ControlButton from "@atoms/ControlButton/ControlButton.vue";
import Header2 from "@components/Typography/Header2.ce.vue";
import Header4 from "@components/Typography/Header4.ce.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import { IProject, IRelatedProjectsProps } from "@/types/components/IRelatedProjectProps";

const carouselRef = ref(null);
const currentIndex = ref(0);
const isLoading = ref(true);
const animationDelay = 300;
const isAnimating = ref(false);
const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);

const props = withDefaults(defineProps<IRelatedProjectsProps>(), {
  isUmbraco: false,
  target: "_self",
  theme: "sand",
});

const { isUmbraco, theme, title, projects, ctaLabel, ctaLink, target, classes } = toRefs(
  props
);

const onEnter = () => {
  if (!theme.value) return;
  changeTheme(theme.value);
};
const onExit = () => {};

onBeforeMount(() => {
  if (theme.value) {
    isLoading.value = false;
  }
});

onMounted(() => {
  if (!isLoading.value) {
    observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
      threshold: 0.6,
    });
  }
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const settings = computed(() => {
  return {
    pagination: false,
    itemsToShow: 1.5,
    snapAlign: "start",
    navigation: false,
  };
});

const breakpoints = computed(() => {
  return {
    768: {
      itemsToShow: 2.1,
    },
  };
});
function next() {
  if (!carouselRef.value || isAnimating.value) return;

  isAnimating.value = true;
  if (currentIndex.value !== projectCards.value.length - 1) {
    currentIndex.value++;
  }
  carouselRef.value.next();

  setTimeout(() => {
    isAnimating.value = false;
  }, animationDelay);
}

function prev() {
  if (!carouselRef.value || isAnimating.value) return;

  isAnimating.value = true;
  if (currentIndex.value !== 0) {
    currentIndex.value--;
  }
  carouselRef.value.prev();

  setTimeout(() => {
    isAnimating.value = false;
  }, animationDelay);
}

const projectCards = computed(() => {
  if (!projects?.value) return [];
  return JSON.parse(projects.value) as IProject[];
});
const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `py-20 ${bgColor} sectionContainer place-content-start ${classes?.value}`;
});
</script>
